import React, { useState } from "react";
import {sortByAttributes} from "../utils/sort.js";
import { FaExclamationTriangle } from 'react-icons/fa';

import { ActiveClaim, LoadingSpinner } from "..";
import { normalizeClaims } from "./NormalizeClaims.js";

import {
    useQuery,
    gql
  } from "@apollo/client";
  
function ActiveClaims(props) {
    const searchText = props.searchText;
    const sortBy = props.sortBy;
    const showExpired = props.showExpired;

    const ACTIVE_CLAIMS = gql`
        query activeClaims($criteria: String) {
            activeClaims (criteria: $criteria) {
                id
                name
                active
                ein
                processingType
                calculationType
                buckets {
                  id
                  name
                  minPercentage
                  active
                  canEdit
                }
                claims {
                    id
                    dos
                    dosLong
                    multiplanPatient
                    multiplanAccount
                    multiplanClaim
                    payor
                    payorClaim
                    multiplanContact
                    multiplanPhone
                    billedCharges
                    initialOffer
                    acceptedOffer
                    status
                    active
                    createUser
                    createDtmEpoch
                    lastUpdateUser
                    lastUpdateDtmEpoch
                    webkey
                    expiringStatus
                    expiringDateLong
                    
                    hasEmrClaimMatch
                    minimumOffer
                    targetOffer
                    calculationDateLong

                    latestOffer {
                        id
                        amount
                        status
                        offerDtmEpoch
                        fee
                        feeStatus
                        acceptedDtmEpoch
                        acceptedSignature
                        rejectedDtmEpoch
                        firstName
                        lastName
                        jobTitle
                        phone
                        emailAddress
                        hasConfirmationImage
                        latestComment {
                            id
                            counterAmount
                            status
                            commentDtmEpoch
                            sendCommentDtmEpoch
                        }
                    }
                    offers {
                        id
                        amount
                        status
                        offerDtmEpoch
                        fee
                        feeStatus
                        acceptedDtmEpoch
                        acceptedSignature
                        rejectedDtmEpoch
                        firstName
                        lastName
                        jobTitle
                        phone
                        emailAddress
                        hasConfirmationImage
                        latestComment {
                            id
                            counterAmount
                            status
                            commentDtmEpoch
                            sendCommentDtmEpoch
                        }
                    }
                    patient {
                        id
                        firstName
                        lastName
                        payor
                        bucketId
                        active
                    }
                }
            }
        }`;

    const searchCriteria = {
        search: searchText,
        showExpired: showExpired
    }
    const { loading, error, data } = useQuery(ACTIVE_CLAIMS, {variables: {"criteria": JSON.stringify(searchCriteria)}});
    
    if (loading) {
        return (            
            <LoadingSpinner active={true} text="test loader" className="loading-overlay"></LoadingSpinner>
        )
    }
    if (error) {
        console.error('ActiveClaims GraphQL error:', error);
        return <></>;
    }
    
    if (!data || !data.activeClaims) {
        console.warn('ActiveClaims: No data available', { data });
        return <></>;
    }
    
    // Debug: Log full response data (for analysis)
    console.log('📦 Full GraphQL Response Data:', JSON.stringify(data, null, 2));
    
    // Debug: Log claims with null/empty offers
    if (data && data.activeClaims) {
        data.activeClaims.forEach((provider, provIdx) => {
            if (provider.claims) {
                provider.claims.forEach((claim, claimIdx) => {
                    const hasEmptyOffers = !claim.offers || claim.offers.length === 0;
                    const hasNullLatestOffer = !claim.latestOffer;
                    if (hasEmptyOffers && hasNullLatestOffer) {
                        console.warn(`⚠️ Claim with null/empty offers found:`, {
                            providerIndex: provIdx,
                            providerName: provider.name,
                            claimIndex: claimIdx,
                            claimId: claim.id,
                            patientName: claim.patient ? `${claim.patient.firstName} ${claim.patient.lastName}` : 'N/A',
                            offers: claim.offers,
                            latestOffer: claim.latestOffer
                        });
                    }
                });
            }
        });
    }

    if (!Array.isArray(data.activeClaims)) {
        console.error('ActiveClaims: data.activeClaims is not an array', { data });
        return <></>;
    }
    
    // Debug: Find claims with null/empty offers
    console.log('🔍 Analyzing claims for null offers...');
    const problematicClaims = [];
    data.activeClaims.forEach((provider, provIdx) => {
        if (provider.claims && Array.isArray(provider.claims)) {
            provider.claims.forEach((claim, claimIdx) => {
                const hasEmptyOffers = !claim.offers || claim.offers.length === 0;
                const hasNullLatestOffer = !claim.latestOffer;
                if (hasEmptyOffers && hasNullLatestOffer) {
                    const problematicClaim = {
                        'Provider': provider.name || 'N/A',
                        'Provider ID': provider.id || 'N/A',
                        'Claim ID': claim.id || 'N/A',
                        'Patient': claim.patient ? `${claim.patient.firstName} ${claim.patient.lastName}` : 'N/A',
                        'DOS': claim.dos || 'N/A',
                        'Offers Array': claim.offers ? `[${claim.offers.length} items]` : 'null/undefined',
                        'Latest Offer': claim.latestOffer ? 'exists' : 'null',
                        'Billed Charges': claim.billedCharges || 'N/A'
                    };
                    problematicClaims.push(problematicClaim);
                    
                    console.warn(`⚠️ PROBLEMATIC CLAIM FOUND:`, {
                        providerIndex: provIdx,
                        providerName: provider.name,
                        providerId: provider.id,
                        claimIndex: claimIdx,
                        claimId: claim.id,
                        patientName: claim.patient ? `${claim.patient.firstName} ${claim.patient.lastName}` : 'N/A',
                        dos: claim.dos,
                        offers: claim.offers,
                        latestOffer: claim.latestOffer,
                        fullClaim: claim
                    });
                }
            });
        }
    });
    
    // Summary table of problematic claims
    if (problematicClaims.length > 0) {
        console.group('📊 SUMMARY: Claims with Null/Empty Offers');
        console.table(problematicClaims);
        console.log(`\n⚠️ Found ${problematicClaims.length} claim(s) with both empty offers array AND null latestOffer.`);
        console.log('These claims would have caused the "Cannot read properties of null (reading \'amount\')" error.');
        console.groupEnd();
    } else {
        console.log('✅ No claims found with null/empty offers.');
    }
    
    let providerClaims = [];
    providerClaims = data.activeClaims;

    let claimsAndBuckets;
    try {
        claimsAndBuckets = normalizeClaims(providerClaims);
    } catch (err) {
        console.error('ActiveClaims: Error normalizing claims', err);
        return <></>;
    }
    
    if (!claimsAndBuckets || !claimsAndBuckets.claims) {
        console.warn('ActiveClaims: normalizeClaims returned invalid data', { claimsAndBuckets });
        return <></>;
    }
    
    let claims = claimsAndBuckets.claims;
    let buckets = claimsAndBuckets.buckets;
    
    if (!Array.isArray(claims)) {
        console.error('ActiveClaims: claims is not an array', { claims });
        return <></>;
    }
    
    try {
        sortByAttributes(claims, sortBy);
    } catch (err) {
        console.error('ActiveClaims: Error sorting claims', err);
        // Continue with unsorted claims
    }
    
    if (claims.length==0) {
        return (<h3 className="mt-3">No Claims Found</h3>);
    }
    
  return (
    <>
        <table className="table table-striped w-auto">
            <thead>
                <tr>
                    <th scope="col" style={{textAlign: "center"}}><FaExclamationTriangle style={{color: "red", fontSize: "28px"}} /></th>
                    <th scope="col">Expiration Date</th>
                    <th scope="col">
                        Payor/Claim/MP/Account
                    </th>
                    <th scope="col">Provider</th>
                    <th scope="col">Patient / DOS</th>
                    <th scope="col">Billed</th>
                    <th scope="col">Minimum</th>
                    <th scope="col">Target</th>
                    <th scope="col">Current Offer</th>
                    <th scope="col">Counter</th>                    
                    <th scope="col">Status</th>
                </tr>
            </thead>
            <tbody>
                {
                    claims.map((claim, id) => {
                        return (<ActiveClaim claim={claim} key={id} />);
                    })
                }
            </tbody>
        </table>
    </>
  );
}

//

export default ActiveClaims;
